import { useState, useCallback, SyntheticEvent } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import CommentPage from "./CommentPage";
import ScorePage from "./ScorePage";
import { submitNpsSurvey } from "src/pods/npsSurvey";
import { closeWebview } from "src/pods/webviewInit";
import { logNpsSurveyEvent } from "src/utils/logEvent";

type SurveyContainerProps = {
  innerHeight: string | number;
  submitNpsSurvey: (score: number | null, comment: string) => void;
};

const mapDispatchToProps = {
  submitNpsSurvey,
};

function SurveyContainer({ innerHeight, submitNpsSurvey }: SurveyContainerProps) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [page, setPage] = useState("SCORE_PAGE");

  const selectScore = useCallback(
    (score: number) => {
      logNpsSurveyEvent({ eventName: "score.tap" });
      setScore(score);
    },
    [setScore],
  );

  const submitScore = useCallback(() => {
    logNpsSurveyEvent({ eventName: "score_page.submit.tap" });
    setPage("COMMENT_PAGE");
  }, [setPage]);

  const updateComment = useCallback(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      setComment(event.currentTarget.value);
    },
    [setComment],
  );

  const submitSurvey = useCallback(
    (copyType: string) => {
      logNpsSurveyEvent({ eventName: `comment_page.${copyType}.submit.tap`, metadata: { enteredComment: !!comment } });
      submitNpsSurvey(score, comment);
      closeWebview();
    },
    [submitNpsSurvey, score, comment],
  );

  const closeSurvey = useCallback(() => {
    if (page === "COMMENT_PAGE") {
      logNpsSurveyEvent({ eventName: `${page.toLowerCase()}.close`, metadata: { enteredComment: !!comment } });
    } else {
      logNpsSurveyEvent({ eventName: `${page.toLowerCase()}.close` });
    }
    submitNpsSurvey(score, comment);
    closeWebview();
  }, [submitNpsSurvey, score, comment, page]);

  switch (page) {
    case "COMMENT_PAGE":
      return (
        <CommentPage
          comment={comment}
          updateComment={updateComment}
          score={score}
          submitSurvey={submitSurvey}
          closeSurvey={closeSurvey}
          innerHeight={innerHeight}
        />
      );
    default:
      return (
        <ScorePage
          score={score}
          selectScore={selectScore}
          submitScore={submitScore}
          closeSurvey={closeSurvey}
          innerHeight={innerHeight}
        />
      );
  }
}

export default compose<SurveyContainerProps, Omit<SurveyContainerProps, "submitNpsSurvey">>(
  connect(() => ({}), mapDispatchToProps),
)(SurveyContainer);
